<template>
<div id="blast">

  <Panel v-if="$apollo.loading">
    Blast Loading
  </Panel>

  <Panel v-else>
    <template slot="panel__filters">
      <span>Showing</span>
      <PanelDropdown
        :inline="true"
        :items="filterItems"
      >
        <template slot="panel-dropdown__button">
          {{filterDisplay}}
        </template>
      </PanelDropdown>
      <span>in this blast</span>
    </template>
    <template slot="panel__header">
      <div class="text-black font-normal text-3xl">
        Blast by {{blastPage.blast.teacherPhoneNumber.displayName}}
        <span class="text-xl font-light">
          {{ formattedDatestamp(blastPage.blast.insertedAt) }} {{ formattedTimestamp(blastPage.blast.insertedAt) }}
        </span>
      </div>
    </template>

    <div v-if="blastPage.blast.processingFailedAt">
      <p>
        This blast failed and was not delivered to any recipients
      </p>
    </div>

    <div v-if="blastPage.communicationList.list.length === 0">
      <p>
        No messages have the status {{ $route.query.status }}
      </p>
    </div>

    <div v-else-if="blastPage.blast.processedAt">
      <router-link
        class="flex text-black text-base mx-4 border rounded p-4 my-4"
        v-for="message in blastPage.communicationList.list"
        :key="message.id"
        :to="{name: 'homeNumber', params: {homeNumberId: message.conversation.homeNumber.id}}"
      >
        <div class="w-1/4 overflow-x-hidden">
          {{numberDisplay(message.conversation.homeNumber)}}
        </div>
        <div class="w-1/4 overflow-x-hidden">
          {{message.conversation.homeNumber.formattedNumber}}
        </div>
        <div class="w-1/4 overflow-x-hidden whitespace-nowrap" style="text-overflow: ellipsis">
          <span class="font-medium text-base pr-1 w-6 inline-block">
            {{ getCanonicalLanguageTag(message) }}
          </span>
          <span>
            {{getCanonicalBodyText(message)}}
          </span>
        </div>
        <!-- received queued sending sent delivered failed -->
        <div
          class="w-1/4 overflow-x-hidden text-right"
          :class="messageStatusClass(message)"
        >
          {{capitalize(message.status)}}
        </div>
      </router-link>
    </div>

    <div v-else>
      This blast is queued and waiting to be processed.
    </div>
  </Panel>

</div>
</template>

<script>
import _get             from 'lodash-es/get'
import _capitalize      from 'lodash-es/capitalize'
import _find            from 'lodash-es/find'
import parse            from 'date-fns/parse'
import isToday          from 'date-fns/is_today'
import isYesterday      from 'date-fns/is_yesterday'
import { preloadMany }  from '@/preloader.js'
import { tz }           from '@/tz.js'
import BlastPageQuery   from '@/queries/BlastPage.gql'
import Panel            from '@/components/Panel.vue'
import PanelDropdown    from '@/components/PanelDropdown.vue'
import Query            from '@/components/Query.vue'

const preloadQueries = (to) => {
  var pageNumber

  if (to.query.page) {
    pageNumber = parseInt(to.query.page)
  }

  return [
    {
      query: BlastPageQuery,
      queryName: 'BlastPageQuery',
      variables: {
        blastId: to.params.blastId,
        tz: tz(),
        status: to.query.status,
        page: pageNumber || 1
      },
      fetchPolicy: 'network-only'
    }
  ]
}


export default {
  components: {
    Panel,
    PanelDropdown,
    Query
  },

  props: {
    blastId: {
      required: true
    }
  },

  data() {
    return {
      blastPage: undefined
    }
  },

  beforeRouteEnter(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next
    })
  },

  beforeRouteUpdate(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next,
      mountedVm: this
    })
  },

  apollo: {
    blastPage() {
      return {
        query: BlastPageQuery,
        variables() {
          return {
            blastId: this.blastId,
            tz: tz(),
            status: this.$route.query.status,
            page: this.page
          }
        },
        update: (data) => {
          return {
            blast: data.blast,
            communicationList: data.communicationList
          }
        },
        fetchPolicy: 'cache-first'
      }
    }
  },

  computed: {

    filterDisplay() {
      if (this.$route.query.status === undefined) {
        return 'all messages'
      } else {
        return _get(this, '$route.query.status') + ' messages'
      }
    },

    filterItems() {

      var items = [
        {text: 'all messages', to: {name: 'blast', params: { blastId: this.blastId }}},
        {text: 'queued messages', to: {name: 'blast', params: { blastId: this.blastId }, query: {status: 'queued'}}},
        {text: 'sending messages', to: {name: 'blast', params: { blastId: this.blastId }, query: {status: 'sending'}}},
        {text: 'delivered messages', to: {name: 'blast', params: { blastId: this.blastId }, query: {status: 'delivered'}}},
        {text: 'failed messages', to: {name: 'blast', params: { blastId: this.blastId }, query: {status: 'failed'}}}
      ]

      return items
    },

    page() {
      if (this.$route.query.page) {
        return parseInt(this.$route.query.page)
      } else {
        return 1
      }
    }

  },

  methods: {
    capitalize(string) {
      return _capitalize(string)
    },

    formattedDatestamp(dateStamp) {
      if (isToday(dateStamp)) {
        return "today"
      } else if (isYesterday(dateStamp)) {
        return "yesterday"
      } else {
        return 'on ' + parse(dateStamp).toLocaleString('en-US', {weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'})
      }
    },

    formattedTimestamp(dateStamp) {
      return 'at ' + parse(dateStamp).toLocaleString('en-US', {hour: 'numeric', minute: 'numeric'})
    },

    numberDisplay(homeNumber) {
      return _get(homeNumber, 'label')
        || _get(homeNumber, 'formattedNumber')
        || _get(homeNumber, 'number')
    },

    getCanonicalBody(message) {
      return _find(message.bodies, 'isCanon')
    },

    getCanonicalBodyText(message) {
      return this.getCanonicalBody(message).text
    },

    getCanonicalLanguageTag(message) {
      return _get(this.getCanonicalBody(message), 'homeLanguage.language')
    },

    messageStatusClass(message) {
      return {
        'text-cardstock-red-500 font-bold': message.status === 'failed',
        'text-cardstock-blue': message.status === 'queued',
        'text-cardstock-blue': message.status === 'sending',
        'text-cardstock-green': message.status === 'sent',
        'text-cardstock-green': message.status === 'delivered'
      }
    }
  }

}
</script>
